import type { KeyboardEvent, MouseEvent } from 'react';
/**
 * t254u: a feed card opens its item from anywhere in its content (title, description, the space around them), not
 * just the title. The card listens for clicks; a click on an inner control (button, link, input, role=button/link,
 * a person, a building, a menu...) or inside a [data-no-card-open] region is that control's and never also opens
 * the item. Clicks from portals (sheets) and the release of a text selection are ignored too. Text marked
 * [data-card-open] (a role="link" description) counts as the card's own.
 */
const CONTROL = 'a,button,input,textarea,select,label,summary,video,audio,[role="button"],[role="link"],[role="menuitem"],[role="menu"],[role="checkbox"],[role="switch"],[role="tab"],[role="option"],[role="dialog"],[contenteditable="true"],[contenteditable=""],[data-no-card-open]';

export function cardClickOpens(e: MouseEvent<HTMLElement>): boolean {
  if (e.defaultPrevented || e.button !== 0) return false;
  const card = e.currentTarget;
  const t = e.target;
  if (!(t instanceof Element) || !card.contains(t)) return false;
  const hit = t.closest(CONTROL);
  if (hit && hit !== card && card.contains(hit) && !hit.hasAttribute('data-card-open')) return false;
  const sel = typeof window !== 'undefined' ? window.getSelection?.() : null;
  if (sel && !sel.isCollapsed && sel.toString().trim() && sel.anchorNode && card.contains(sel.anchorNode)) return false;
  return true;
}

/** Spread on the card: opens on a click in its content (see cardClickOpens). */
export function cardOpenHandlers(onOpen: () => void) {
  return { onClick: (e: MouseEvent<HTMLElement>) => { if (cardClickOpens(e)) onOpen(); } };
}

/** Spread on a card's description: a keyboard-reachable link to the item (the click itself goes through the card). */
export function cardTextProps(onOpen: () => void, label?: string) {
  return {
    role: 'link' as const, tabIndex: 0, 'data-card-open': '', 'aria-label': label,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); } },
  };
}
